import { Download } from '@carbon/icons-react';
import { Button, InlineNotification } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useState } from 'react';
import { hoApi } from '../api';

type CsvPath =
  | '/api/v1/promotions/promotions/{promotionId}/codes.csv'
  | '/api/v1/membership/gift-voucher-batches/{batchId}/vouchers.csv'
  | '/api/v1/crm/segments/{id}/export.csv';

/**
 * Downloads a CSV of coupon codes or gift vouchers (ADR 0043), to print or send, or a segment's people (ADR 0080). The API gives them
 * only to those allowed, and the file is made in the browser from the response, never from a link with a token.
 */
export function CsvDownloadButton({
  label,
  path,
  params,
  query,
  fileName,
}: {
  label: string;
  path: CsvPath;
  params: Record<string, string>;
  /** Query parameters, e.g. a segment export's reveal. */
  query?: Record<string, string | boolean>;
  fileName: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const { data, response } = (await hoApi.GET(
        path as never,
        { params: { path: params, query }, parseAs: 'blob' } as never,
      )) as {
        data?: unknown;
        response: Response;
      };
      if (!response.ok || !(data instanceof Blob)) throw new Error(String(response.status));
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button kind="tertiary" size="md" renderIcon={Download} disabled={busy} onClick={() => void download()}>
        {label}
      </Button>
      {failed && (
        <InlineNotification
          kind="error"
          lowContrast
          title={t('promotions.downloadFailed')}
          onClose={() => setFailed(false)}
        />
      )}
    </>
  );
}
